import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import dayjs from 'dayjs';

import { mapStateToProps, mapDispatchToProps } from '../../Store';
import PromotionDropdown from '../PromotionDropdown';

const displayDate = (date, timeZone = 'CENTRAL') => {
    // example: Tuesday, June 8, 2021
    return (
        <>
            <span className='d-block'>{`${dayjs(date).format('dddd')}, ${dayjs(date).format('MMMM D, YYYY')}, `}</span>
            <span className='d-block'>{`${dayjs(date).format('hh:mm:ss A')} ${timeZone}`}</span>
        </>
    );
};

const Entries = ({ entry, loading, actions }) => {
    const pagelimit = 5;
    const [promotionId, setPromotionId] = useState(0);
    const [page, setPage] = useState(1);
    const [seeMore, setSeeMore] = useState(false);

    const [activeColumn, setActiveColumn] = useState(2);
    const [numberOfCols] = useState(4);

    const handlePromotionChange = (event) => {
        setPromotionId(event.target.value);
        // reset page to 1 when promotion changes
        setPage(1);
    };

    const nextColumn = () => {
        setActiveColumn((activeColumn) => activeColumn + 1);
    };

    const previousColumn = () => {
        setActiveColumn((activeColumn) => activeColumn - 1);
    };

    const seeMoreHandler = (currentPage) => (event) => setPage(currentPage + 1);

    useEffect(() => {
        if (entry.promotionId != promotionId || page == 1) {
            actions.entryActions.reset();
        }
        actions.entryActions.getEntries({ promotion_id: promotionId, page: page, limit: pagelimit });
    }, [page, promotionId, entry?.last_entry]);

    useEffect(() => {
        let { totalRecords, page } = entry;
        if (totalRecords / (pagelimit * page) > 1) {
            setSeeMore(true);
        } else {
            setSeeMore(false);
        }
    }, [entry.page]);

    // sort function for entry dates
    const sortEntriesByTimestamp = (entryA, entryB) => {
        return moment(entryB.entry_date).format('X') - moment(entryA.entry_date).format('X');
    };

    const generateEntriesTableContent = (entries = []) => {
        if (entries.length === 0) {
            let msg = 'No entries submitted.';
            if (loading.actions['getEntries']) {
                msg = 'Loading entries...';
            }
            return (
                <tbody>
                    <tr key='0'>
                        <td data-title='Entries' colSpan='4' className='text-center'>
                            <em>{msg}</em>
                        </td>
                    </tr>
                </tbody>
            );
        } else {
            // note we are spreading entries into a new array so we don't mutate the entries array in state
            return (
                <tbody>
                    {[...entries].sort(sortEntriesByTimestamp).map(({ ticket_number, entry_date_local_tz, points, promotion_name }) => {
                        return (
                            <tr key={ticket_number}>
                                <td data-title='Entry Code' className='td-entry-code first-col'>
                                    {ticket_number}
                                </td>
                                <td data-title='Promotion Name' className={`td-promotion other-col ${activeColumn == 2 ? 'show' : ''}`}>
                                    {promotion_name}
                                </td>
                                <td data-title='Date &amp; Time' className={`td-entry-date other-col ${activeColumn == 3 ? 'show' : ''}`}>
                                    {displayDate(entry_date_local_tz)}
                                </td>
                                <td data-title='Entries Awarded' className={`td-entries other-col ${activeColumn == 4 ? 'show' : ''}`}>
                                    {points}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            );
        }
    };

    return (
        <div className='row'>
            <div className='form-container mx-auto'>
                <PromotionDropdown
                    className={`m-2`}
                    label='Promotion Name'
                    id='promotion'
                    name='promotion'
                    changeHandler={handlePromotionChange}
                    selected={promotionId}
                    placeholder='All promotions'
                />
            </div>
            {Number(entry?.promotionId) !== 0 && (
                <div className='col-12 text-right px-5'>
                    <h2>TOTAL ENTRIES: {entry?.currentPoints}</h2>
                </div>
            )}
            <div className='table-scroll-container'>
                <button id='prevColumn' className='shift lft' disabled={activeColumn <= 2} onClick={previousColumn}>
                    &lt;
                </button>
                <button id='nextColumn' className='shift rgt' disabled={activeColumn >= numberOfCols} onClick={nextColumn}>
                    &gt;
                </button>
                <table id='entryTable' className='entry-table table-panelled'>
                    <thead>
                        <tr>
                            <th className='td-entry-code first-col'>
                                <span>Entry</span>
                                <span>Code</span>
                            </th>
                            <th className={`td-promotion other-col ${activeColumn == 2 ? 'show' : ''}`}>
                                <span>Promotion</span>
                                <span>Name</span>
                            </th>
                            <th className={`td-entry-date other-col ${activeColumn == 3 ? 'show' : ''}`}>
                                <span>Date &amp;</span>
                                <span>Time</span>
                            </th>
                            <th className={`td-entries other-col ${activeColumn == 4 ? 'show' : ''}`}>
                                <span>Entries</span>
                                <span>Awarded</span>
                            </th>
                        </tr>
                    </thead>
                    {generateEntriesTableContent(entry?.entries)}
                </table>
            </div>
            <div className='col-12 my-3 text-center'>
                <button disabled={!seeMore} className='theme-btn theme-btn-secondary text-uppercase mx-auto' onClick={seeMoreHandler(entry?.page ?? 1)}>
                    {loading.actions['getEntries'] && <span className='loading-icon ellipses' role='status' aria-hidden='true'></span>}
                    See More
                </button>
            </div>
        </div>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Entries);
